import React, { useState, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { StytchProvider } from '@stytch/react';
import { StytchUIClient } from '@stytch/vanilla-js';
import { AuthProvider, useAuth } from '@/modules/auth/ui/AuthProvider';
import AuthenticatePage from '@/pages/AuthenticatePage';
import { LandingPage } from '@/modules/landing/ui/LandingPage';
import { Footer } from '@/shared/components/Footer';
import * as Sentry from '@sentry/browser';

function AppContent() {
  const { user, loading, signOut } = useAuth();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [showWelcome, setShowWelcome] = useState(true);

  // Simple routing
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Auto-hide welcome screen for returning users
  useEffect(() => {
    if (user) {
      const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
      if (hasSeenWelcome) {
        setShowWelcome(false);
      }
    }
  }, [user]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('mobile-user-dropdown');
      const dropdownButton = event.target.closest('[data-dropdown-toggle]');
      
      if (dropdown && !dropdown.contains(event.target) && !dropdownButton) {
        dropdown.classList.add('hidden');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-4 border-t-transparent border-purple-600 rounded-full animate-spin"></div>
          <span className="text-lg text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  // Show authenticate page
  if (currentPath === '/authenticate') {
    return <AuthenticatePage />;
  }

  // Show welcome screen for newly authenticated users
  if (user && showWelcome) {
    // Check if user has seen welcome screen before
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    if (hasSeenWelcome) {
      setShowWelcome(false);
      return null; // Will re-render with main app
    }

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="py-20">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
            <img 
              src="https://supabase.zapt.ai/storage/v1/render/image/public/icons/c7bd5333-787f-461f-ae9b-22acbc0ed4b0/55145115-0624-472f-96b9-d5d88aae355f.png?width=80&height=80" 
              alt="App Traction" 
              className="w-20 h-20 mx-auto mb-6"
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">🎉 Welcome to the Waitlist!</h2>
            <p className="text-gray-600 mb-6">
              You're now on the waitlist for App Traction. We'll notify you via email when we launch.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Signed in as: {user.email}
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => {
                  localStorage.setItem('hasSeenWelcome', 'true');
                  setShowWelcome(false);
                }}
                className="w-full bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer"
              >
                Continue to App
              </button>
              <button 
                onClick={() => {
                  try {
                    signOut();
                    setShowWelcome(false);
                  } catch (error) {
                    console.error('Error signing out:', error);
                  }
                }}
                className="w-full bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show landing page for non-authenticated users
  return (
    <div className="min-h-screen flex flex-col">
      <nav className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="https://supabase.zapt.ai/storage/v1/render/image/public/icons/c7bd5333-787f-461f-ae9b-22acbc0ed4b0/55145115-0624-472f-96b9-d5d88aae355f.png?width=40&height=40" 
                alt="App Traction" 
                className="w-8 h-8 sm:w-10 sm:h-10"
              />
              <span className="text-lg sm:text-2xl font-bold text-white">
                <span className="sm:hidden">App Traction</span>
                <span className="hidden sm:inline">App Traction</span>
              </span>
            </div>
            {user && (
              <div className="flex items-center gap-2 sm:gap-4">
                {/* Desktop welcome message */}
                <span className="text-white text-xs sm:text-sm hidden sm:inline">
                  Welcome, {user.email.split('@')[0]}
                </span>
                
                {/* Mobile compact user display */}
                <div className="sm:hidden flex items-center gap-1">
                  <div className="relative">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    {/* Status dot overlay */}
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-purple-700 animate-pulse"></div>
                  </div>
                </div>
                
                {/* Desktop status indicator */}
                <div className="hidden sm:flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-white text-sm font-medium">Waitlist</span>
                </div>
                
                {/* Desktop sign out button */}
                <button 
                  onClick={() => {
                    try {
                      signOut();
                    } catch (error) {
                      console.error('Error signing out:', error);
                    }
                  }}
                  className="hidden sm:block bg-white text-purple-900 font-medium px-4 py-2 text-sm rounded-full hover:bg-purple-50 transition-colors cursor-pointer"
                >
                  Sign Out
                </button>
                
                {/* Mobile dropdown menu */}
                <div className="sm:hidden relative">
                  <button 
                    data-dropdown-toggle
                    onClick={() => {
                      const dropdown = document.getElementById('mobile-user-dropdown');
                      dropdown.classList.toggle('hidden');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const dropdown = document.getElementById('mobile-user-dropdown');
                        dropdown.classList.toggle('hidden');
                      }
                    }}
                    aria-label="User menu"
                    aria-expanded="false"
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  
                  {/* Dropdown menu */}
                  <div 
                    id="mobile-user-dropdown"
                                         className="hidden absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  >
                                         <div className="px-4 py-3 border-b border-gray-100">
                       <div className="flex items-center gap-3">
                         <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                           <span className="text-white text-lg font-semibold">
                             {user.email.charAt(0).toUpperCase()}
                           </span>
                         </div>
                         <div className="flex-1 min-w-0">
                           <p className="text-sm font-medium text-gray-900 truncate">{user.email.split('@')[0]}</p>
                           <p className="text-xs text-gray-500 truncate">{user.email}</p>
                         </div>
                       </div>
                     </div>
                    <div className="px-4 py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-sm text-gray-700">On Waitlist</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">We'll notify you when we launch!</p>
                    </div>
                    <button 
                      onClick={() => {
                        try {
                          signOut();
                          document.getElementById('mobile-user-dropdown').classList.add('hidden');
                        } catch (error) {
                          console.error('Error signing out:', error);
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
      
      {/* Landing page content with CTA buttons */}
      <div onClick={(e) => {
        const buttonText = e.target.textContent || e.target.closest('button')?.textContent;
        if (buttonText?.includes('Get Your $19 Audit') || 
            buttonText?.includes('Get Your Audit Report') || 
            e.target.closest('button')?.textContent?.includes('Get Your $19 Audit')) {
          e.preventDefault();
          if (user) {
            // User is already authenticated, show they're on waitlist
            alert('🎉 You\'re already on the waitlist! We\'ll notify you when App Traction launches.');
          } else {
            // User not authenticated, take them to sign up
            window.location.href = '/authenticate';
          }
        }
      }}>
        <LandingPage />
      </div>
      
      <Footer />
      <Analytics />
    </div>
  );
}

function App() {
  const publicToken = import.meta.env.VITE_PUBLIC_STYTCH_PUBLIC_TOKEN;

  // Create StytchUIClient properly according to docs
  const stytchOptions = {
    cookieOptions: {
      opaqueTokenCookieName: "stytch_session",
      jwtCookieName: "stytch_session_jwt",
      path: "",
      availableToSubdomains: false,
      domain: "",
    }
  };

  const stytchClient = new StytchUIClient(publicToken, stytchOptions);

  return (
    <StytchProvider stytch={stytchClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </StytchProvider>
  );
}

export default App;